import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { StepperModule } from 'primeng/stepper';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { AppButton } from '../button/app-button';
import { PreviewNode } from './preview-node/preview-node';

@Component({
  selector: 'app-preview',
  imports: [
    CommonModule,
    StepperModule,
    PreviewNode,
    SelectModule,
    AppButton,
    FormsModule,
    TranslationPipe,
    InputTextModule,
    CheckboxModule
  ],
  templateUrl: './preview.html',
  styleUrl: './preview.scss'
})
export class Preview {
  selectedStep: any;
  steps = model<any[]>([]);
  showSearch = signal<boolean>(false);
  showDropdown = signal<boolean>(false);
  showPreviewLabel = input<boolean>(false);
  onStepClickEvent = output<any>();
  private searchSubject$ = new Subject<string>();
  searchValue: string = '';
  showCheckbox: boolean = false;
  showActionBtns: boolean = false;
  isLoading: boolean = false;

  config = inject(DynamicDialogConfig, { optional: true });
  ref = inject(DynamicDialogRef, { optional: true });
  isDialog: boolean = false;
  selected: any = null;
  selectAll: boolean = false;
  hasExpandableNodes = computed(() => this.steps().some((step) => step.children?.length > 0));

  ngOnInit() {
    // set loading to disable btns while saving
    if (this.config?.data.loading$) {
      this.config?.data.loading$.subscribe((val: boolean) => {
        this.isLoading = val;
      });
    }

    //if initialised as dialog
    if (this.steps().length === 0 && this.config?.data) {
      this.isDialog = true;
      this.showSearch.set(true);

      if (this.config?.data.showCheckbox) {
        this.showCheckbox = true;
      }
      //confirm and cancel btns
      if (this.config?.data.showActionBtns) {
        this.showActionBtns = true;
      }

      if (this.config?.data.showDropdown) {
        this.showDropdown.set(true);
      }

      if (!this.showDropdown()) {
        //for search function: observable for parent component to load data
        this.searchSubject$ = this.config.data.searchSubject$;

        //subscribe to data passed from parent component
        const steps$ = this.config.data.steps$ as BehaviorSubject<any[]>;
        steps$.subscribe((list) => {
          this.steps.set(list); // update tree dynamically
        });
      }
    }
  }

  ngOnChanges() {}

  //#region Select All
  onSelectAllChange() {
    this.steps().forEach((step) => this.updateChecked(step, this.selectAll));
  }

  private updateChecked(node: any, checked: boolean) {
    node.checked = checked;

    node.children?.forEach((child: any) => {
      this.updateChecked(child, checked);
    });
  }

  // Handle parent node click (toggle expand/collapse)
  onNodeClick(step: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    // Toggle the expanded state
    step.expanded = !step.expanded;

    // Update the steps signal to trigger change detection
    this.steps.set([...this.steps()]);
  }

  // Handle child step click (selection)
  onStepClick(event: any): void {
    this.selectedStep = event;
    if (this.isDialog) {
      this.ref?.close(this.selectedStep);
    }

    this.onStepClickEvent.emit(event);
  }

  onSearchInput() {
    const steps$ = this.config?.data.steps$ as BehaviorSubject<any[]>;

    steps$.pipe(take(1)).subscribe((list) => {
      const keyword = this.searchValue?.trim().toLowerCase();

      if (!keyword) {
        this.steps.set(list);
        return;
      }

      const filterNodes = (nodes: any[]): any[] =>
        nodes.flatMap((node) => {
          const children = node.children?.length ? node.children : (node.childrens ?? []);

          const matches = [node.description, node.name, node.label].some(
            (value) => typeof value === 'string' && value.toLowerCase().includes(keyword)
          );

          // A matching parent keeps its whole branch.
          if (matches) {
            return [
              {
                ...node,
                children,
                expanded: children.length > 0
              }
            ];
          }

          // A matching child keeps and expands its parent.
          const matchingChildren = filterNodes(children);

          return matchingChildren.length
            ? [
                {
                  ...node,
                  children: matchingChildren,
                  expanded: true
                }
              ]
            : [];
        });

      this.steps.set(filterNodes(list));
    });
  }

  onSearchChange(value: string) {
    if (!value.trim()) {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.searchValue = '';
    this.onSearchInput();
  }

  onConfirm() {
    this.config?.data.event$.next({ save: true, data: this.steps() });
  }

  onCancel() {
    this.config?.data.event$.next({ save: false, data: null });
  }

  dropdownOptions() {
    return this.config?.data.dropdown$.value;
  }

  onOptionChange(event: any) {
    this.searchValue = '';
    this.selected = event.value;

    this.searchSubject$ = this.config?.data.searchSubject$;

    const steps$ = this.config?.data.steps$ as BehaviorSubject<any[]>;
    steps$.subscribe((list) => {
      const filtered = list.filter((item: any) => item.type && item.type === this.selected.value);
      this.steps.set(filtered); // update tree dynamically
    });
  }
}
